import { useMediaQuery } from '@ltvco/refresh-lib/theme';
import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { ChatInput, MessagesContainer } from 'components/ChatBV/components';
import { LOADING_STATUS } from 'components/ChatBV/constants';
import { ChatContext, ChatLayoutContext } from 'components/ChatBV/context';
import { useChatForm, useReportState } from 'components/ChatBV/hooks';

export const ChatPage = () => {
  const { messages, loadingStatus } = useContext(ChatContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSendMessage } = useChatForm(() => {});
  const reportState = useReportState();
  const { closeAllPanels: reportCloseAllPanels } = reportState;
  const {
    showLanding,
    setShowLanding,
    isReportOpen,
    setIsReportOpen,
    setHasCompletedAnimation,
    isLogoAnimating,
    setIsLogoAnimating,
    logoAnimationPhase,
    setLogoAnimationPhase,
    setCloseAllPanels,
  } = useContext(ChatLayoutContext);
  const [shouldCloseReport, setShouldCloseReport] = useState(false);
  const prevPathRef = useRef<string>(location.pathname);

  const isConversationRoute = location.pathname === '/chatbv/chat/conversation';
  const isBaseChatRoute = location.pathname === '/chatbv/chat';

  // Register closeAllPanels function in context so ChatLayoutProvider can use it
  useEffect(() => {
    setCloseAllPanels(() => {
      reportCloseAllPanels();
      setIsReportOpen(false);
    });
  }, [reportCloseAllPanels, setCloseAllPanels, setIsReportOpen]);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;

    if (isBaseChatRoute && prevPath === '/chatbv/chat/conversation') {
      reportCloseAllPanels();
      setIsReportOpen(false);
    }
  }, [
    location.pathname,
    isBaseChatRoute,
    reportCloseAllPanels,
    setIsReportOpen,
  ]);

  // Use MUI's useMediaQuery with custom breakpoint to target exactly 1024px
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');

  // Handle transition from landing page to chat
  useEffect(() => {
    if (messages.length > 0 && showLanding && !isConversationRoute) {
      const lastMessage = messages[messages.length - 1];
      const isUserMessage = lastMessage?.role === 'user';

      if (isUserMessage) {
        // Start logo animation
        setIsLogoAnimating(true);
        setLogoAnimationPhase('moving-up');

        // After logo moves up, move it to the left
        setTimeout(() => {
          setLogoAnimationPhase('moving-left');
        }, 800);

        // After logo moves left, transition to chat
        setTimeout(() => {
          setLogoAnimationPhase('header');
          setTimeout(() => {
            setHasCompletedAnimation(true);
            setShowLanding(false);
            setIsLogoAnimating(false);
            navigate(`/chatbv/chat/conversation${location.search}`, {
              replace: false,
            });
          }, 0);
        }, 1200);
      }
    } else if (messages.length === 0 && !showLanding) {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
    }
  }, [
    messages.length,
    showLanding,
    messages,
    isConversationRoute,
    navigate,
    setIsLogoAnimating,
    setLogoAnimationPhase,
    setHasCompletedAnimation,
    setShowLanding,
  ]);

  return (
    <>
      <MessagesContainer
        reportState={reportState}
        messages={messages}
        loadingStatus={loadingStatus}
        onReportStateChange={setIsReportOpen}
        shouldCloseReport={shouldCloseReport}
        onReportCloseHandled={() => setShouldCloseReport(false)}
        showLanding={showLanding}
        isLogoAnimating={isLogoAnimating}
        logoAnimationPhase={logoAnimationPhase}
        onSendMessage={handleSendMessage}
        sx={{
          ...(isMobileBreakpoint &&
            !isReportOpen && {
              paddingTop: '0',
            }),
        }}
      />
      {messages.length > 0 && !showLanding && (
        <>
          {isMobileBreakpoint && (
            <ChatInput
              isReportOpen={isReportOpen}
              isLanding={false}
              isAnimating={isLogoAnimating}
              animationPhase={logoAnimationPhase}
              isLoading={loadingStatus !== LOADING_STATUS.IDLE}
            />
          )}
        </>
      )}
    </>
  );
};
